import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2, GripVertical } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolve(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

/**
 * MultiImageUpload — permite enviar várias imagens de uma só vez.
 * value é sempre um array de URLs (string[]).
 */
export default function MultiImageUpload({ value = [], onChange, testId, maxFiles = 50 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [dragIndex, setDragIndex] = useState(null);

  const list = Array.isArray(value) ? value : [];

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) {
      toast.error("Selecione arquivos de imagem");
      return;
    }
    const oversized = arr.filter((f) => f.size > 8 * 1024 * 1024);
    if (oversized.length) {
      toast.error(`${oversized.length} arquivo(s) excedem 8 MB e foram ignorados`);
    }
    const valid = arr.filter((f) => f.size <= 8 * 1024 * 1024);
    if (list.length + valid.length > maxFiles) {
      toast.error(`Máximo ${maxFiles} imagens por álbum`);
      return;
    }
    if (!valid.length) return;

    setUploading(true);
    setProgress({ done: 0, total: valid.length });
    const uploaded = [];
    for (let i = 0; i < valid.length; i++) {
      try {
        const url = await uploadOne(valid[i]);
        uploaded.push(url);
      } catch (e) {
        toast.error(`Falha no upload de ${valid[i].name}`);
      }
      setProgress({ done: i + 1, total: valid.length });
    }
    if (uploaded.length) {
      onChange([...list, ...uploaded]);
      toast.success(`${uploaded.length} imagem(ns) carregada(s)`);
    }
    setUploading(false);
    setProgress({ done: 0, total: 0 });
  };

  const onFileChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    const next = list.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const move = (from, to) => {
    if (from === to || to < 0 || to >= list.length) return;
    const next = list.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div data-testid={testId ? `${testId}-wrapper` : undefined}>
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        data-testid={testId ? `${testId}-dropzone` : undefined}
        className="border border-dashed border-[color:var(--marupiara-gold)]/40 hover:border-[color:var(--marupiara-gold)] bg-black/20 hover:bg-black/30 transition-colors cursor-pointer py-6 px-4 flex flex-col items-center gap-2 text-center"
      >
        {uploading ? (
          <>
            <Loader2 size={22} className="animate-spin text-[color:var(--marupiara-gold)]" />
            <span className="text-sm text-white/80">Enviando {progress.done}/{progress.total}…</span>
          </>
        ) : (
          <>
            <ImageIcon size={22} className="text-[color:var(--marupiara-gold)]" />
            <span className="text-sm text-white">
              <span className="font-medium">Clique</span> ou arraste várias imagens
            </span>
            <span className="text-[11px] text-white/50">PNG, JPG, WEBP, GIF, AVIF • máx 8 MB por arquivo</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFileChange}
        className="hidden"
        data-testid={testId ? `${testId}-file-input` : undefined}
      />

      {list.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-white/60 uppercase tracking-wider">{list.length} imagem(ns) no álbum</span>
            <button
              type="button"
              onClick={() => onChange([])}
              data-testid={testId ? `${testId}-clear-all` : undefined}
              className="text-[11px] text-red-300 hover:text-red-200 flex items-center gap-1"
            >
              <X size={11} /> Limpar tudo
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {list.map((url, idx) => (
              <div
                key={`${url}-${idx}`}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, idx);
                  setDragIndex(null);
                }}
                className="relative group aspect-square overflow-hidden bg-black/30 border border-white/10 rounded"
                data-testid={testId ? `${testId}-item-${idx}` : undefined}
              >
                <img src={resolve(url)} alt={`img-${idx}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <span className="absolute top-1 left-1 text-[10px] text-white/80 bg-black/60 px-1.5 py-0.5 rounded">
                    <GripVertical size={10} className="inline" /> {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                    className="p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded"
                    title="Remover"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={testId ? `${testId}-add-more` : undefined}
              className="aspect-square border border-dashed border-white/20 hover:border-[color:var(--marupiara-gold)] text-white/60 hover:text-[color:var(--marupiara-gold)] flex flex-col items-center justify-center gap-1 rounded"
            >
              <Upload size={16} />
              <span className="text-[10px]">Adicionar</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
